import { resolveFieldOptions } from "@/utils/field-options";
import type { DynamicOption } from "@/hooks/use-dynamic-options";
import { FieldDataType, FormCondition } from "@/types/dynamic-detail";
import { looseEquals } from "@/utils/display-condition";

/**
 * Client side of `crm.object_config.form_conditions` (G1): `visible_when`, `required_when` and
 * `disabled_conditions` read the values being edited, plus the form's context parameters. The
 * server evaluates the same conditions in `form-action`, so this only decides what is *shown*.
 *
 * `option_attribute` reads an attribute of the chosen option: the `option_metadata` the field
 * declares, or the `metadata` `form-options` serves with each option of a relation field.
 * A list value (multiple combobox) matches `eq`, `in` and `truthy` when one of its items does;
 * `neq`, `not_in` and `falsy` (« not set ») are their negations.
 */

export type OptionAttributeReader = (fieldName: string, value: unknown, attribute: string) => unknown;

type OptionWithMetadata = { value: string | null; label: string; metadata?: Record<string, unknown> | null };

function isTruthy(value: unknown): boolean {
    if (typeof value === "string") return value.trim() !== "";
    if (Array.isArray(value)) return value.length > 0;
    return Boolean(value);
}

function leftValue(condition: FormCondition, scope: Record<string, unknown>, readAttribute?: OptionAttributeReader): unknown {
    const value = scope[condition.field];
    const attribute = condition.option_attribute;
    if (!attribute) return value;
    if (!readAttribute) return undefined;
    if (Array.isArray(value)) {
        return value.filter(item => item !== null && item !== undefined && item !== "").map(item => readAttribute(condition.field, item, attribute));
    }
    if (value === null || value === undefined || value === "") return undefined;
    return readAttribute(condition.field, value, attribute);
}

export function conditionMatches(condition: FormCondition, scope: Record<string, unknown>, readAttribute?: OptionAttributeReader): boolean {
    const left = leftValue(condition, scope, readAttribute);
    const items = Array.isArray(left) ? left : null;
    const operator = condition.operator ?? "eq";

    if (operator === "truthy" || operator === "falsy") {
        const hit = items ? items.some(isTruthy) : isTruthy(left);
        return operator === "truthy" ? hit : !hit;
    }
    if (operator === "in" || operator === "not_in") {
        const candidates: unknown[] = Array.isArray(condition.value) ? condition.value : [];
        const found = (item: unknown) => candidates.some(candidate => looseEquals(item, candidate));
        const hit = items ? items.some(found) : found(left);
        return operator === "in" ? hit : !hit;
    }
    const right = condition.value_from_field ? scope[condition.value_from_field] : condition.value;
    const hit = items ? items.some(item => looseEquals(item, right)) : looseEquals(left, right);
    return operator === "eq" ? hit : !hit;
}

/** Every condition matches. No condition at all holds. */
export function conditionsHold(conditions: FormCondition[] | null | undefined, scope: Record<string, unknown>, readAttribute?: OptionAttributeReader): boolean {
    return (conditions ?? []).every(condition => conditionMatches(condition, scope, readAttribute));
}

/**
 * Reads the attributes of the option chosen through the field's effective options (`resolveFieldOptions`):
 * the descriptor's `option_metadata` first, then each option's `metadata`, from the list served with the
 * descriptor (a step's) or the one loaded (`form-options`). Give it the descriptors on screen
 * (`currentDescriptors`), so a list a step serves is read.
 *
 * `fields` are the siblings a condition names: the form's fields at the root, a fieldset's `subfields`
 * inside one, with `descriptorPrefix` the fieldset's descriptor path (`options` are keyed by descriptor path).
 */
export function optionAttributeReader(fields: FieldDataType[], options: Record<string, OptionWithMetadata[]>, descriptorPrefix?: string): OptionAttributeReader {
    const byName = new Map(fields.map(field => [field.field_name, field]));
    return (fieldName, value, attribute) => {
        const field = byName.get(fieldName);
        const path = descriptorPrefix ? `${descriptorPrefix}.${fieldName}` : fieldName;
        const metadata = field
            ? resolveFieldOptions(field, path, { dynamicOptions: options as Record<string, DynamicOption[]> }).metadata
            : Object.fromEntries((options[path] ?? []).filter(option => option.metadata).map(option => [String(option.value), option.metadata as Record<string, unknown>]));
        return metadata[String(value)]?.[attribute];
    };
}

/**
 * A `multiple` select / combobox holds its choices as one comma-separated string (`Combobox`): the
 * conditions read them as a list, item by item, as `form-action` does.
 */
export function splitMultipleValues(fields: FieldDataType[], values: Record<string, unknown>): Record<string, unknown> {
    const result = { ...values };
    fields.forEach(field => {
        const value = result[field.field_name];
        if (field.multiple && (field.field_type === "combobox" || field.field_type === "select") && typeof value === "string") {
            result[field.field_name] = value
                .split(",")
                .map(item => item.trim())
                .filter(Boolean);
        }
    });
    return result;
}

/** Whether a field list carries conditions that read the values being edited. */
export function hasFormConditions(fields: FieldDataType[]): boolean {
    return fields.some(field => Boolean(field.visible_when?.length || field.required_when?.length));
}

/**
 * The fields of a form under the current values: a field whose `visible_when` does not hold is
 * left out (neither rendered, validated nor submitted), one whose `required_when` holds comes back
 * `required` — exactly what `form-action` does with the submitted values.
 */
export function resolveConditionalFields(fields: FieldDataType[], values: Record<string, unknown>, readAttribute?: OptionAttributeReader): FieldDataType[] {
    const scope = splitMultipleValues(fields, values);
    return fields
        .filter(field => !field.visible_when?.length || conditionsHold(field.visible_when, scope, readAttribute))
        .map(field => (field.required_when?.length && !field.required && conditionsHold(field.required_when, scope, readAttribute) ? { ...field, required: true } : field));
}

/** Only the values of the fields left in the form, so a hidden field is not submitted. */
export function keepVisibleValues(visibleFields: FieldDataType[], values: Record<string, unknown>): Record<string, unknown> {
    const keep = new Set(visibleFields.map(field => field.field_name));
    return Object.fromEntries(Object.entries(values).filter(([name]) => keep.has(name)));
}

/**
 * What a live preview (`FormConfig.preview_on`) is asked again on: the values of `names` among those posted
 * (`keepVisibleValues`). A field that appears counts as a change: a value set while its field was hidden (a
 * prefill before the funding is chosen) reaches the preview once the field is shown.
 */
export function previewKeyOf(names: string[], posted: Record<string, unknown>): string {
    return JSON.stringify(names.map(name => (name in posted ? (posted[name] ?? null) : { hidden: true })));
}

/** The values of `names` found in a query string (`FormConfig.context_params`). */
export function readContextParams(names: string[] | undefined, search: URLSearchParams): Record<string, string> {
    const context: Record<string, string> = {};
    (names ?? []).forEach(name => {
        const value = search.get(name);
        if (value) context[name] = value;
    });
    return context;
}
